import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Phone, MapPin } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';
import { useLanguage } from '@/lib/i18n';

function LangSwitch() {
  const { lang, setLang } = useLanguage();
  const langs = ['fr', 'en', 'es'];
  return (
    <div className="flex items-center rounded-full border border-garden-200 overflow-hidden text-xs font-medium" data-testid="lang-switch">
      {langs.map(l => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 transition-colors ${lang === l ? 'bg-garden-800 text-white' : 'text-charcoal hover:bg-garden-100'}`}
          data-testid={`lang-${l}`}
        >{l.toUpperCase()}</button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.getSettings().then(r => setSettings(r.data || {})).catch(() => {});
  }, []);

  const ordersEnabled = settings.ordersModuleEnabled === true;

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/carte', label: t('nav.menu') },
    ...(ordersEnabled ? [{ href: '/commander', label: t('nav.order') }] : []),
    { href: '/galerie', label: t('nav.gallery') },
    { href: '/avis', label: t('nav.reviews') },
    { href: '/reservation', label: t('nav.reservation') },
  ];

  return (
    <nav className="fixed top-4 left-1/2 -translate-x-1/2 w-[95%] max-w-5xl z-50 rounded-full border border-white/40 shadow-sm backdrop-blur-md bg-cream/80 px-6 py-3 flex items-center justify-between" data-testid="navbar">
      <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
        {settings.restaurant_logo_url ? (
          <img
            src={settings.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.restaurant_logo_url}` : settings.restaurant_logo_url}
            alt={settings.name || 'Cote Terrasse'}
            className="h-8 w-auto object-contain"
            data-testid="navbar-logo-img"
          />
        ) : (
          <span className="font-accent text-2xl text-garden-800 hover:text-garden-600 transition-colors">
            {settings.name || 'Cote Terrasse'}
          </span>
        )}
      </Link>

      {/* Desktop nav */}
      <div className="hidden md:flex items-center gap-1">
        {navLinks.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            data-testid={`navbar-${link.href.replace('/', '') || 'home'}`}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              location.pathname === link.href
                ? 'bg-garden-800 text-white'
                : 'text-charcoal hover:bg-garden-100 hover:text-garden-800'
            }`}
          >
            {link.label}
          </Link>
        ))}
      </div>

      <div className="hidden md:flex items-center gap-3">
        <LangSwitch />
        <a href="tel:0952675289" className="flex items-center gap-1 text-sm text-garden-800 hover:text-garden-600" data-testid="navbar-phone">
          <Phone size={14} />
          <span>09 52 67 52 89</span>
        </a>
        <Link to="/reservation">
          <Button data-testid="navbar-reserve-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full px-6 text-sm">
            {t('nav.reserve')}
          </Button>
        </Link>
      </div>

      {/* Mobile nav */}
      <div className="flex items-center gap-2 md:hidden">
        <LangSwitch />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="navbar-mobile-toggle" className="p-2 text-garden-800">
              <Menu size={24} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-cream w-[280px]">
            <div className="flex flex-col gap-4 mt-8">
              {settings.restaurant_logo_url ? (
                <img
                  src={settings.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.restaurant_logo_url}` : settings.restaurant_logo_url}
                  alt={settings.name || 'Cote Terrasse'}
                  className="h-10 w-auto object-contain self-start mb-4"
                />
              ) : (
                <span className="font-accent text-2xl text-garden-800 mb-4">{settings.name || 'Cote Terrasse'}</span>
              )}
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setOpen(false)}
                  className={`px-4 py-3 rounded-xl text-base font-medium transition-all ${
                    location.pathname === link.href
                      ? 'bg-garden-800 text-white'
                      : 'text-charcoal hover:bg-garden-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-4 pt-4 border-t border-garden-200">
                <a href="tel:0952675289" className="flex items-center gap-2 text-garden-800 mb-3">
                  <Phone size={16} /> 09 52 67 52 89
                </a>
                <a href="https://www.google.com/maps/place/198+Cours+de+l'Argonne,+33000+Bordeaux" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-garden-800 text-sm">
                  <MapPin size={16} /> 198 Cours de l'Argonne
                </a>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}
